import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  LogOut,
  FileText,
  Download,
  Search,
  BarChart3,
  DollarSign,
  AlertCircle,
  Menu,
  X,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { IDSubmission } from '../types';
import { useSubmissions } from '../hooks/useSubmissions';
import { getCountryByCode, getAllCountries } from '../utils/countries';
import toast from 'react-hot-toast';

interface AdminDashboardProps {
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const { submissions, loading, getSubmissions, updateSubmissionStatus, approveSubmission, rejectSubmission } = useSubmissions();
  const [selectedSubmission, setSelectedSubmission] = useState<IDSubmission | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'rejected' | 'approved_pending_payment' | 'paid'>('all');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'submissions' | 'payments' | 'submission-detail'>('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [showReasonModal, setShowReasonModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<{id: string; action: string} | null>(null);
  const [showGeolocationJson, setShowGeolocationJson] = useState<boolean>(false);
  const [copiedGeoData, setCopiedGeoData] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  // Get all countries for validation
  const countries = getAllCountries();

  // Helper functions for new data structure
  const getDocumentTypesText = (documents: any[]) => {
    if (!documents || !Array.isArray(documents) || documents.length === 0) {
      return 'No documents';
    }
    
    const validDocuments = documents.filter(doc => doc && doc.type);
    
    if (validDocuments.length === 0) {
      return 'No documents';
    }
    
    return validDocuments
      .map(doc => (doc.type || 'unknown').replace('_', ' '))
      .join(', ');
  };

  const capitalizeText = (text: string) => {
    return text.replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const copyGeolocationData = async (data: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopiedGeoData(true);
      toast.success('Geolocation data copied to clipboard');
      setTimeout(() => setCopiedGeoData(false), 2000);
    } catch (err) {
      toast.error('Failed to copy data');
    }
  };

  // Optimized refresh with debouncing and smart refresh logic
  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefresh;
    
    // Prevent spam refreshing (minimum 2 seconds between refreshes)
    if (timeSinceLastRefresh < 2000 && !isRefreshing) {
      toast.error(`Please wait ${Math.ceil((2000 - timeSinceLastRefresh) / 1000)} seconds before refreshing again`);
      return;
    }

    setIsRefreshing(true);
    setLastRefresh(now);
    
    try {
      await getSubmissions();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [getSubmissions, lastRefresh, isRefreshing]);

  const openSubmissionDetail = (submission: IDSubmission) => {
    setSelectedSubmission(submission);
    setActiveTab('submission-detail');
    setSidebarOpen(false);
  };

  const handleApproval = (submissionId: string) => {
    setPaymentAmount(''); // No default amount
    setAdminNotes('');
    setPendingAction({id: submissionId, action: 'approve'});
    setShowReasonModal(true);
  };

  const handleRejection = (submissionId: string) => {
    setRejectionReason('');
    setAdminNotes('');
    setPendingAction({id: submissionId, action: 'reject'});
    setShowReasonModal(true);
  };

  const executeAction = async () => {
    if (!pendingAction) return;

    const { id, action } = pendingAction;
    
    try {
      if (action === 'approve') {
        const amount = parseFloat(paymentAmount);
        if (amount <= 0) {
          toast.error('Payment amount must be greater than 0');
          return;
        }
        await approveSubmission(id, amount, adminNotes);
        
        // Update selected submission status if it matches
        if (selectedSubmission && selectedSubmission.id === id) {
          setSelectedSubmission({ 
            ...selectedSubmission, 
            status: 'approved_pending_payment',
            payment_amount: amount,
            admin_notes: adminNotes 
          });
        }
        
      } else if (action === 'reject') {
        if (!rejectionReason.trim()) {
          toast.error('Rejection reason is required');
          return;
        }
        await rejectSubmission(id, rejectionReason, adminNotes);
        
        // Update selected submission status if it matches
        if (selectedSubmission && selectedSubmission.id === id) {
          setSelectedSubmission({ 
            ...selectedSubmission, 
            status: 'rejected',
            rejection_reason: rejectionReason,
            admin_notes: adminNotes 
          });
        }
      }

      setShowReasonModal(false);
      setPendingAction(null);
      setPaymentAmount('');
      setRejectionReason('');
      setAdminNotes('');
      
      // Refresh data after action
      await getSubmissions();
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  // Copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const cancelAction = () => {
    setShowReasonModal(false);
    setPendingAction(null);
    setPaymentAmount('');
    setRejectionReason('');
    setAdminNotes('');
  };

  useEffect(() => {
    getSubmissions();
  }, [getSubmissions]);

  // Simple filtering logic without sorting
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      // Filter by status
      if (filter !== 'all' && submission.status !== filter) {
        return false;
      }
      
      // Filter by search term
      if (searchTerm !== '') {
        const searchLower = searchTerm.toLowerCase();
        const matchesId = submission.id.toLowerCase().includes(searchLower);
        const matchesName = submission.personalInfo?.fullName?.toLowerCase().includes(searchLower);
        const matchesEmail = submission.personalInfo?.email?.toLowerCase().includes(searchLower);
        const matchesDocType = submission.documents?.some(doc => 
          doc.type.toLowerCase().includes(searchLower)
        );
        
        if (!matchesId && !matchesName && !matchesEmail && !matchesDocType) {
          return false;
        }
      }
      
      return true;
    });
  }, [submissions, filter, searchTerm]);

  // Memoize stats calculations to avoid recalculation on every render
  const stats = useMemo(() => ({
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
    approved_pending_payment: submissions.filter(s => s.status === 'approved_pending_payment').length,
    paid: submissions.filter(s => s.status === 'paid').length,
  }), [submissions]);

  // Memoize payout calculations
  const { totalPayouts, pendingPayouts } = useMemo(() => ({
    totalPayouts: submissions
      .filter(s => s.status === 'paid')
      .reduce((sum, s) => sum + (s.payment_amount || 0), 0),
    pendingPayouts: submissions
      .filter(s => s.status === 'approved_pending_payment') 
      .reduce((sum, s) => sum + (s.payment_amount || 0), 0)
  }), [submissions]);
  // Analytics data previously computed here (submission/document/payment distributions)
  // were removed because they were not used in this build. Re-add if analytics views are
  // restored and need chart data.

  const handleStatusUpdate = async (id: string, status: 'rejected' | 'approved_pending_payment' | 'paid') => {
    try {
      await updateSubmissionStatus(id, status);
      toast.success(`Submission ${status.replace(/_/g, ' ')} successfully`);
      
      // Update the selected submission if it matches the updated one
      if (selectedSubmission && selectedSubmission.id === id) {
        const updatedSubmission = { ...selectedSubmission, status };
        setSelectedSubmission(updatedSubmission);
      }
      
      await getSubmissions(); // Refresh data
    } catch (error) {
      toast.error('Failed to update submission status');
    }
  };

  // Memoized CSV export function to avoid recreating on every render
  const exportData = useCallback(() => {
    const csvHeaders = [
      'ID',
      'Full Name',
      'Email',
      'Country',
      'State',
      'City',
      'Address',
      'Postal Code',
      'Document Types',
      'Document Count',
      'Document 1 Type',
      'Document 1 Front URL',
      'Document 1 Back URL',
      'Document 2 Type',
      'Document 2 Front URL',
      'Document 2 Back URL',
      'Document 3 Type',
      'Document 3 Front URL',
      'Document 3 Back URL',
      'Status',
      'Payment Amount',
      'PayPal Name',
      'PayPal Email',
      'IP Address',
      'Source Country',
      'Geo City',
      'Geo Region',
      'Geo Country',
      'Geo ISP',
      'Geo Coordinates',
      'Created At',
      'Updated At',
      'Reviewed At',
      'Payment Processed At',
      'Rejection Reason',
      'Admin Notes'
    ].join(',');

    const csvContent = csvHeaders + '\n' +
      filteredSubmissions.map(sub => {
        const personalInfo = sub.personalInfo || {};
        const documents = sub.documents || [];
        const fields = [
          sub.id,
          `"${personalInfo.fullName || ''}"`,
          `"${personalInfo.email || ''}"`,
          `"${personalInfo.country || ''}"`,
          `"${personalInfo.state || ''}"`,
          `"${personalInfo.city || ''}"`,
          `"${personalInfo.address || ''}"`,
          `"${personalInfo.postalCode || ''}"`,
          `"${getDocumentTypesText(sub.documents)}"`,
          documents.length,
          // Document 1
          `"${documents[0]?.type || ''}"`,
          `"${documents[0]?.frontImage?.url || ''}"`,
          `"${documents[0]?.backImage?.url || ''}"`,
          // Document 2
          `"${documents[1]?.type || ''}"`,
          `"${documents[1]?.frontImage?.url || ''}"`,
          `"${documents[1]?.backImage?.url || ''}"`,
          // Document 3
          `"${documents[2]?.type || ''}"`,
          `"${documents[2]?.frontImage?.url || ''}"`,
          `"${documents[2]?.backImage?.url || ''}"`,
          sub.status,
          sub.payment_amount || '',
          `"${sub.paypal_name || ''}"`,
          `"${sub.paypal_email || ''}"`,
          `"${sub.ip_address || ''}"`,
          `"${sub.source_country || ''}"`,
          `"${sub.geolocation_data?.city || ''}"`,
          `"${sub.geolocation_data?.regionName || ''}"`,
          `"${sub.geolocation_data?.country || ''}"`,
          `"${sub.geolocation_data?.isp || ''}"`,
          `"${sub.geolocation_data ? `${sub.geolocation_data.lat.toFixed(4)}, ${sub.geolocation_data.lon.toFixed(4)}` : ''}"`,
          new Date(sub.created_at).toISOString(),
          sub.updated_at ? new Date(sub.updated_at).toISOString() : '',
          sub.reviewed_at ? new Date(sub.reviewed_at).toISOString() : '',
          sub.payment_processed_at ? new Date(sub.payment_processed_at).toISOString() : '',
          `"${sub.rejection_reason || ''}"`,
          `"${sub.admin_notes || ''}"`
        ];
        return fields.join(',');
      }).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Build a safe, descriptive filename: submissions_<filter>[_search-<term>]_YYYY-MM-DDTHH-MM-SS.csv
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const safeSearch = searchTerm ? `_search-${searchTerm.replace(/[^a-z0-9_-]/gi, '_').slice(0, 30)}` : '';
    const filename = `submissions_${filter || 'all'}${safeSearch}_${timestamp}.csv`;

    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredSubmissions, filter, searchTerm]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'approved_pending_payment': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'approved_pending_payment': return <AlertCircle className="w-4 h-4" />;
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Memoize analytics calculations for dashboard
  const analyticsData = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const todaySubmissions = submissions.filter(s => new Date(s.created_at) >= today);
    const yesterdaySubmissions = submissions.filter(s => {
      const date = new Date(s.created_at);
      return date >= yesterday && date < today;
    });
    const weekSubmissions = submissions.filter(s => new Date(s.created_at) >= thisWeek);
    const monthSubmissions = submissions.filter(s => new Date(s.created_at) >= thisMonth);

    // Calculate average processing time
    const processedSubmissions = submissions.filter(s => s.reviewed_at);
    const avgProcessingTime = processedSubmissions.length > 0 
      ? processedSubmissions.reduce((sum, s) => {
          const submitted = new Date(s.created_at).getTime();
          const reviewed = new Date(s.reviewed_at!).getTime();
          return sum + (reviewed - submitted);
        }, 0) / processedSubmissions.length / (1000 * 60 * 60) // Convert to hours
      : 0;

    // Geographic insights
    const countryStats = submissions.reduce((acc, s) => {
      const country = s.personalInfo?.country || 'Unknown';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topCountries = Object.entries(countryStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Document type insights
    const docTypeStats = submissions.reduce((acc, s) => {
      const types = s.documents?.map(d => d.type) || [];
      types.forEach(type => {
        acc[type] = (acc[type] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);

    // Total documents across all submissions (used for percentage calculations)
    const totalDocCount = Object.values(docTypeStats).reduce((sum, n) => sum + n, 0);

    return {
      todaySubmissions,
      yesterdaySubmissions,
      weekSubmissions,
      monthSubmissions,
      avgProcessingTime,
      topCountries,
      docTypeStats,
      totalDocCount
    };
  }, [submissions]);

  const renderDashboard = () => {
    const {
      todaySubmissions,
      yesterdaySubmissions,
      weekSubmissions,
      monthSubmissions,
      avgProcessingTime,
      topCountries,
      docTypeStats,
      totalDocCount
    } = analyticsData;

    // Payment insights
    const avgPayment = submissions
      .filter(s => s.payment_amount && s.payment_amount > 0)
      .reduce((sum, s, _, arr) => sum + (s.payment_amount || 0) / arr.length, 0);

    const conversionRate = submissions.length > 0 
      ? ((stats.paid + stats.approved_pending_payment) / submissions.length * 100)
      : 0;

    return (
      <div className="space-y-6">
        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Submissions</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats.total}</p>
                <div className="flex items-center mt-2">
                  <span className="text-xs text-green-600 font-medium">
                    +{todaySubmissions.length} today
                  </span>
                </div>
              </div>
              <div className="relative">
                <FileText className="w-8 h-8 md:w-10 md:h-10 text-blue-500" />
                {todaySubmissions.length > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border-l-4 border-yellow-500 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Processing Time</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">
                  {avgProcessingTime > 0 ? `${avgProcessingTime.toFixed(1)}h` : 'N/A'}
                </p>
                <div className="flex items-center mt-2">
                  <span className="text-xs text-yellow-600 font-medium">
                    {stats.pending} pending review
                  </span>
                </div>
              </div>
              <div className="relative">
                <Clock className="w-8 h-8 md:w-10 md:h-10 text-yellow-500" />
                {stats.pending > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-bounce"></div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Approval Rate</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">{conversionRate.toFixed(1)}%</p>
                <div className="flex items-center mt-2">
                  <span className="text-xs text-green-600 font-medium">
                    ${totalPayouts.toFixed(0)} paid out
                  </span>
                </div>
              </div>
              <div className="relative">
                <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-green-500" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border-l-4 border-purple-500 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Payment</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">
                  ${avgPayment > 0 ? avgPayment.toFixed(2) : '0.00'}
                </p>
                <div className="flex items-center mt-2">
                  <span className="text-xs text-purple-600 font-medium">
                    ${pendingPayouts.toFixed(0)} pending
                  </span>
                </div>
              </div>
              <div className="relative">
                <DollarSign className="w-8 h-8 md:w-10 md:h-10 text-purple-500" />
                {stats.approved_pending_payment > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activity Overview</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Today</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{width: `${Math.min(100, (todaySubmissions.length / Math.max(1, yesterdaySubmissions.length)) * 100)}%`}}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{todaySubmissions.length}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">This Week</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{width: `${Math.min(100, (weekSubmissions.length / Math.max(1, submissions.length)) * 100)}%`}}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{weekSubmissions.length}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">This Month</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full" 
                      style={{width: `${Math.min(100, (monthSubmissions.length / Math.max(1, submissions.length)) * 100)}%`}}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{monthSubmissions.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Geographic Insights */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Top Countries</h3>
            </div>
            <div className="p-6 space-y-3">
              {topCountries.length > 0 ? topCountries.map(([country, count], index) => (
                <div key={country} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      index === 0 ? 'bg-blue-500' : 
                      index === 1 ? 'bg-green-500' : 
                      index === 2 ? 'bg-yellow-500' : 
                      index === 3 ? 'bg-purple-500' : 'bg-gray-400'
                    }`}></div>
                    <span className="text-sm font-medium text-gray-900">{country}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          index === 0 ? 'bg-blue-500' : 
                          index === 1 ? 'bg-green-500' : 
                          index === 2 ? 'bg-yellow-500' : 
                          index === 3 ? 'bg-purple-500' : 'bg-gray-400'
                        }`}
                        style={{width: `${(count / Math.max(...topCountries.map(([,c]) => c))) * 100}%`}}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{count}</span>
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Document Type Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Document Types Overview</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(docTypeStats).map(([type, count]) => (
                <div key={type} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{capitalizeText(type.replace('_', ' '))}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {totalDocCount > 0 ? ((count / totalDocCount) * 100).toFixed(1) : '0.0'}% of total
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={() => {setActiveTab('submissions'); setFilter('pending'); setSidebarOpen(false);}}
                className="flex items-center justify-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-yellow-800 font-medium">Review Pending ({stats.pending})</span>
              </button>
              <button
                onClick={() => {setActiveTab('payments'); setSidebarOpen(false);}}
                className="flex items-center justify-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">Process Payments ({stats.approved_pending_payment})</span>
              </button>
              <button
                onClick={() => {setActiveTab('submissions'); setSidebarOpen(false);}}
                className="flex items-center justify-center p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <FileText className="w-5 h-5 text-purple-600 mr-2" />
                <span className="text-purple-800 font-medium">All Submissions</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSubmissions = () => (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="approved_pending_payment">Approved Pending Payment</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <button
            onClick={exportData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block lg:hidden space-y-4">
        {loading ? (
          // Loading skeleton for mobile cards
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-40"></div>
                </div>
                <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          ))
        ) : (
          filteredSubmissions.map((submission) => (
          <div key={submission.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  ID: <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(submission.id, 'ID');
                    }}
                    className="hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded transition-colors cursor-pointer"
                    title="Click to copy full ID"
                  >
                    {submission.id.slice(0, 8)}...
                  </button>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {capitalizeText(getDocumentTypesText(submission.documents))}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {submission.personalInfo?.fullName} • {submission.personalInfo?.country}
                </p>
              </div>
              <div className="text-right flex flex-col items-end space-y-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(submission.status)}`}>
                  {getStatusIcon(submission.status)}
                  <span className="ml-1">{submission.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                </span>
                {submission.payment_amount && (
                  <span className="text-sm font-bold text-green-600">
                    ${submission.payment_amount}
                  </span>
                )}
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mb-3 space-y-1">
              <p>
                <span className="font-medium">Date:</span> {new Date(submission.created_at).toLocaleDateString()}
              </p>
              {submission.ip_address && (
                <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded inline-block">
                  IP: {submission.ip_address}
                </p>
              )}
              {submission.geolocation_data ? (
                <p>
                  <span className="font-medium">Location:</span> {submission.geolocation_data.city}, {submission.geolocation_data.regionName}, {submission.geolocation_data.country}
                  {submission.geolocation_data.isp && (
                    <span className="text-xs text-gray-500 ml-2">({submission.geolocation_data.isp})</span>
                  )}
                </p>
              ) : submission.source_country && (
                <p>
                  <span className="font-medium">Source:</span> {submission.source_country}
                </p>
              )}
              {submission.admin_notes && (
                <p>
                  <span className="font-medium">Admin Notes:</span> 
                  <span className="text-blue-600"> {submission.admin_notes.length > 40 ? submission.admin_notes.substring(0, 40) + '...' : submission.admin_notes}</span>
                </p>
              )}
              {submission.rejection_reason && (
                <p>
                  <span className="font-medium">Rejection:</span> 
                  <span className="text-red-600"> {submission.rejection_reason.length > 40 ? submission.rejection_reason.substring(0, 40) + '...' : submission.rejection_reason}</span>
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => openSubmissionDetail(submission)}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Eye className="w-4 h-4 mr-2" />
                Review
              </button>
            </div>
          </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Personal Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                // Loading skeleton for table rows
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-16 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-3 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-3 bg-gray-200 rounded w-28 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-16 h-8 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : (
                filteredSubmissions.map((submission) => (
                <tr 
                  key={submission.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openSubmissionDetail(submission)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex -space-x-1">
                      {submission.documents && submission.documents.slice(0, 2).map((document, index) => (
                        <div key={index} className="relative">
                          {document.frontImage?.url ? (
                            <img
                              src={document.frontImage.url}
                              alt={`Document ${index + 1}`}
                              className="w-8 h-8 rounded border border-gray-200 object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                              <span className="text-xs text-gray-400">?</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {submission.documents && submission.documents.length > 2 && (
                        <div className="w-8 h-8 rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                          <span className="text-xs text-gray-600">+{submission.documents.length - 2}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(submission.id, 'ID');
                      }}
                      className="hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors cursor-pointer"
                      title="Click to copy full ID"
                    >
                      {submission.id.slice(0, 8)}...
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.personalInfo?.fullName || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {submission.personalInfo?.email || 'N/A'}
                    </div>
                    {submission.personalInfo?.emailVerified && (
                      <span className="inline-flex items-center mt-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {submission.personalInfo?.country || 'N/A'}
                      {submission.personalInfo?.state && `, ${submission.personalInfo.state}`}
                    </div>
                    <div className="text-sm text-gray-500">
                      {submission.personalInfo?.city || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {capitalizeText(getDocumentTypesText(submission.documents))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(submission.status)}`}>
                      {getStatusIcon(submission.status)}
                      <span className="ml-1">{submission.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.payment_amount ? `$${submission.payment_amount}` : (
                        <span className="text-gray-400 italic">Not set</span>
                      )}
                    </div>
                    {(submission.paypal_name || submission.paypal_email) && (
                      <div className="text-xs text-gray-500">
                        {submission.paypal_name && <div>Name: {submission.paypal_name}</div>}
                        {submission.paypal_email && <div>PayPal: {submission.paypal_email}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{new Date(submission.created_at).toLocaleDateString()}</div>
                    <div className="text-xs">{new Date(submission.created_at).toLocaleTimeString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs">
                    <div className="space-y-1">
                      {submission.ip_address && (
                        <div className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                          IP: {submission.ip_address}
                        </div>
                      )}
                      {submission.geolocation_data ? (
                        <div className="text-xs space-y-1">
                          <div className="text-blue-600">
                            📍 {submission.geolocation_data.city}, {submission.geolocation_data.regionName}
                          </div>
                          <div className="text-gray-500">
                            {submission.geolocation_data.country} • {submission.geolocation_data.isp}
                          </div>
                        </div>
                      ) : submission.source_country && (
                        <div className="text-xs text-blue-600">
                          Source: {submission.source_country}
                        </div>
                      )}
                      {submission.admin_notes && (
                        <div className="text-green-600 text-xs">
                          <span className="font-medium">Admin:</span> {submission.admin_notes.length > 20 ? submission.admin_notes.substring(0, 20) + '...' : submission.admin_notes}
                        </div>
                      )}
                      {submission.rejection_reason && (
                        <div className="text-red-600 text-xs">
                          <span className="font-medium">Rejected:</span> {submission.rejection_reason.length > 20 ? submission.rejection_reason.substring(0, 20) + '...' : submission.rejection_reason}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openSubmissionDetail(submission);
                      }}
                      className="text-blue-600 hover:text-blue-900 flex items-center bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Review
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-6">
      {/* Payment Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total Paid Out</h3>
              <p className="text-2xl font-bold text-green-600">${totalPayouts.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{stats.paid} submissions processed</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Pending Payments</h3>
              <p className="text-2xl font-bold text-yellow-600">${pendingPayouts.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{stats.approved_pending_payment} awaiting payment</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Payment Rate</h3>
              <p className="text-2xl font-bold text-blue-600">$5-20</p>
              <p className="text-sm text-gray-500">per approved submission</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {submissions.filter(s => s.status === 'approved_pending_payment').length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-800 font-medium">
                {stats.approved_pending_payment} submissions ready for payment
              </span>
            </div>
            <button
              onClick={() => {
                if (window.confirm(`Are you sure you want to mark all ${stats.approved_pending_payment} submissions as paid?`)) {
                  // Handle bulk payment processing
                  submissions
                    .filter(s => s.status === 'approved_pending_payment')
                    .forEach(s => handleStatusUpdate(s.id, 'paid'));
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Process All Payments
            </button>
          </div>
        </div>
      )}

      {/* Pending Payments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Pending Payments</h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {stats.approved_pending_payment} pending
            </span>
          </div>
        </div>
        
        {/* Mobile Card View */}
        <div className="block lg:hidden divide-y divide-gray-200">
          {submissions.filter(s => s.status === 'approved_pending_payment').map((submission) => (
            <div key={submission.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 font-mono">
                    {submission.id.slice(0, 8)}...
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {capitalizeText(getDocumentTypesText(submission.documents))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Approved: {new Date(submission.reviewed_at || submission.updated_at || submission.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">${submission.payment_amount || 0}</p>
                </div>
              </div>
              
              <div className="flex justify-end items-center">
                <button
                  onClick={() => handleStatusUpdate(submission.id, 'paid')}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Mark as Paid
                </button>
              </div>
            </div>
          ))}
          {submissions.filter(s => s.status === 'approved_pending_payment').length === 0 && (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No pending payments</h3>
              <p className="text-gray-500">All approved submissions have been processed and paid.</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submission ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Approved
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.filter(s => s.status === 'approved_pending_payment').map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-mono text-gray-900">{submission.id.slice(0, 8)}...</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {capitalizeText(getDocumentTypesText(submission.documents))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-green-600">
                      ${submission.payment_amount || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(submission.reviewed_at || submission.updated_at || submission.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleStatusUpdate(submission.id, 'paid')}
                      className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Mark Paid
                    </button>
                  </td>
                </tr>
              ))}
              {submissions.filter(s => s.status === 'approved_pending_payment').length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No pending payments</h3>
                    <p className="text-gray-500">All approved submissions have been processed and paid.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );


  const renderSubmissionDetail = () => {
    if (!selectedSubmission) return null;

    return (
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Submission Details</h1>
              <p className="text-gray-600">ID: {selectedSubmission.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedSubmission.status)}`}>
              {getStatusIcon(selectedSubmission.status)}
              <span className="ml-2">{selectedSubmission.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <p className="text-gray-900 font-medium">{selectedSubmission.personalInfo?.fullName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.email || 'N/A'}</p>
                      {selectedSubmission.personalInfo?.emailVerified && (
                        <span className="inline-flex items-center mt-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </span>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Name</label>
                      <p className="text-gray-900">{selectedSubmission.paypal_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.country || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source Country</label>
                      <p className="text-gray-900">{selectedSubmission.source_country || 'N/A'}</p>
                      {selectedSubmission.source_country && selectedSubmission.personalInfo?.country && (() => {
                        // Compare countries by converting ISO code to name or name to code
                        const sourceCountry = selectedSubmission.source_country;
                        const personalCountry = selectedSubmission.personalInfo.country;
                        
                        // Get country info
                        const sourceCountryInfo = getCountryByCode(sourceCountry.toUpperCase()) || 
                                                 countries.find(c => c.name.toLowerCase() === sourceCountry.toLowerCase());
                        const personalCountryInfo = getCountryByCode(personalCountry.toUpperCase()) || 
                                                   countries.find(c => c.name.toLowerCase() === personalCountry.toLowerCase());
                        
                        // Check if they're different (either by code or name)
                        const isDifferent = sourceCountryInfo?.code !== personalCountryInfo?.code &&
                                          sourceCountryInfo?.name !== personalCountryInfo?.name &&
                                          sourceCountry !== personalCountry;
                        
                        return isDifferent ? (
                          <span className="inline-flex items-center mt-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Location mismatch
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.state || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.city || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.address || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                      <p className="text-gray-900">{selectedSubmission.personalInfo?.postalCode || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Identity Documents</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Document Types: {capitalizeText(getDocumentTypesText(selectedSubmission.documents))}
                </p>
              </div>
              <div className="p-6">
                <div className="space-y-8">
                  {selectedSubmission.documents && selectedSubmission.documents.length > 0 ? (
                    selectedSubmission.documents.map((document, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h5 className="text-md font-medium text-gray-900 mb-4">
                          Document {index + 1}: {capitalizeText((document.type || 'Unknown').replace('_', ' '))}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <p className="text-sm text-gray-600 mb-3 font-medium">Front Side</p>
                            {document.frontImage?.url ? (
                              <img
                                src={document.frontImage.url}
                                alt="Document Front"
                                className="w-full h-64 object-cover border rounded-lg cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                                onClick={() => setSelectedImage(document.frontImage?.url || null)}
                              />
                            ) : (
                              <div className="w-full h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                                <span className="text-gray-500">No front image</span>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-3 font-medium">Back Side</p>
                            {document.backImage?.url ? (
                              <img
                                src={document.backImage.url}
                                alt="Document Back"
                                className="w-full h-64 object-cover border rounded-lg cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                                onClick={() => setSelectedImage(document.backImage?.url || null)}
                              />
                            ) : (
                              <div className="w-full h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                                <span className="text-gray-500">No back image</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p>No documents uploaded</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions & Metadata */}
          <div className="space-y-6">
            {/* Status & Actions Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
              </div>
              <div className="p-6 space-y-4">
                {selectedSubmission.status === 'pending' && (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        handleApproval(selectedSubmission.id);
                      }}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Approve for Payment
                    </button>
                    <button
                      onClick={() => {
                        handleRejection(selectedSubmission.id);
                      }}
                      className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center font-medium"
                    >
                      <XCircle className="w-5 h-5 mr-2" />
                      Reject Submission
                    </button>
                  </div>
                )}

                {selectedSubmission.status === 'approved_pending_payment' && (
                  <button
                    onClick={() => handleStatusUpdate(selectedSubmission.id, 'paid')}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    Mark as Paid (${selectedSubmission.payment_amount || 0})
                  </button>
                )}

                {selectedSubmission.status === 'paid' && (
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="mx-auto h-8 w-8 text-green-500 mb-2" />
                    <p className="text-green-700 font-medium">Payment Completed</p>
                    <p className="text-green-600 text-sm">${selectedSubmission.payment_amount || 0} paid</p>
                  </div>
                )}

                {selectedSubmission.status === 'rejected' && (
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <XCircle className="mx-auto h-8 w-8 text-red-500 mb-2" />
                    <p className="text-red-700 font-medium">Submission Rejected</p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <p className="text-2xl font-bold text-green-600">
                    ${selectedSubmission.payment_amount || 'Not set'}
                  </p>
                </div>
                
                {/* Payment Method Indicator */}
                {selectedSubmission.payment_method && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <div className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800">
                      💳
                      <span className="ml-2">PayPal</span>
                    </div>
                  </div>
                )}

                {/* PayPal Details */}
                {(selectedSubmission.paypal_name || selectedSubmission.paypal_email) ? (
                  <div className="space-y-3">
                    {selectedSubmission.paypal_name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Name</label>
                        <p className="text-gray-900 font-mono text-sm">{selectedSubmission.paypal_name}</p>
                      </div>
                    )}
                    {selectedSubmission.paypal_email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Email</label>
                        <p className="text-gray-900 font-mono text-sm">{selectedSubmission.paypal_email}</p>
                      </div>
                    )}
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center text-blue-800 text-sm">
                        <span className="mr-2">ℹ️</span>
                        <span>Payments will be sent directly to this PayPal account</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No PayPal details provided</div>
                )}
              </div>
            </div>

            {/* Submission Metadata Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-900">Metadata</h3>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 text-sm">
          {/* Dates + IP */}
          <div>
            <span className="text-gray-500">Submitted:</span>{" "}
            <span className="text-gray-900 font-medium">
              {new Date(selectedSubmission.created_at).toLocaleString()}
            </span>
          </div>

          {selectedSubmission.updated_at && (
            <div>
              <span className="text-gray-500">Last Updated:</span>{" "}
              <span className="text-gray-900 font-medium">
                {new Date(selectedSubmission.updated_at).toLocaleString()}
              </span>
            </div>
          )}

          {selectedSubmission.reviewed_at && (
            <div>
              <span className="text-gray-500">Reviewed:</span>{" "}
              <span className="text-gray-900 font-medium">
                {new Date(selectedSubmission.reviewed_at).toLocaleString()}
              </span>
            </div>
          )}

          {selectedSubmission.payment_processed_at && (
            <div>
              <span className="text-gray-500">Payment Processed:</span>{" "}
              <span className="text-gray-900 font-medium">
                {new Date(selectedSubmission.payment_processed_at).toLocaleString()}
              </span>
            </div>
          )}

          <div>
            <span className="text-gray-500">IP Address:</span>{" "}
            <span className="font-mono text-gray-900">
              {selectedSubmission.ip_address || "N/A"}
            </span>
          </div>

          {/* City + Country (if available) */}
          {selectedSubmission.geolocation_data && (
            <div>
              <span className="text-gray-500">Location:</span>{" "}
              <span className="text-gray-900 font-medium">
                {selectedSubmission.geolocation_data.city},{" "}
                {selectedSubmission.geolocation_data.country}
              </span>
            </div>
          )}

          {/* Source fallback */}
          {selectedSubmission.source_country &&
            !selectedSubmission.geolocation_data && (
              <div>
                <span className="text-gray-500">Original Source:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {selectedSubmission.source_country}
                </span>
              </div>
            )}

          {/* Geolocation JSON */}
          {selectedSubmission.geolocation_data && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800">Geolocation Details</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      copyGeolocationData(selectedSubmission.geolocation_data)
                    }
                    className="p-1.5 rounded hover:bg-gray-100 transition"
                    title="Copy raw JSON data"
                  >
                    {copiedGeoData ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                  <button
                    onClick={() => setShowGeolocationJson(!showGeolocationJson)}
                    className="px-2 py-1 text-xs rounded bg-blue-50 hover:bg-blue-100 text-blue-700"
                  >
                    {showGeolocationJson ? "Hide JSON" : "Show JSON"}
                  </button>
                </div>
              </div>

              {showGeolocationJson && (
                <div className="mt-2 bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-60 text-xs font-mono">
                  <pre className="whitespace-pre-wrap text-gray-800">
                    {JSON.stringify(selectedSubmission.geolocation_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>



            {/* Admin Notes & Rejection Reason */}
            {(selectedSubmission.admin_notes || selectedSubmission.rejection_reason) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Admin Information</h3>
                </div>
                <div className="p-6 space-y-4">
                  {selectedSubmission.rejection_reason && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <label className="block text-sm font-medium text-red-700 mb-2">Rejection Reason</label>
                      <p className="text-red-800">{selectedSubmission.rejection_reason}</p>
                    </div>
                  )}
                  {selectedSubmission.admin_notes && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium text-blue-700 mb-2">Admin Notes</label>
                      <p className="text-blue-800">{selectedSubmission.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Mobile Header - Only shows on mobile */}
      <div className="lg:hidden bg-white shadow-md border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-1.5 rounded-lg mr-2">
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">IDsRewards Admin</h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={loading || isRefreshing}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-40 transition-all duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Layout Container */}
      <div className="lg:flex min-h-screen">
        {/* Sidebar - Responsive */}
        <div className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 
          fixed lg:relative 
          inset-y-0 lg:inset-y-auto
          left-0 
          z-50 lg:z-auto
          w-64 lg:w-72 xl:w-80
          bg-white 
          shadow-2xl lg:shadow-sm 
          transition-transform duration-300 ease-in-out
          lg:flex-shrink-0
          overflow-y-auto
          border-r border-gray-200
          flex flex-col
        `}>
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 lg:h-20 px-4 lg:px-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 lg:bg-none">
            <h1 className="text-lg lg:text-xl font-bold text-white lg:text-gray-900">IDsRewards Admin</h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={loading || isRefreshing}
                className="hidden lg:block p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 rounded-md text-white hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 lg:px-6 py-6">
            <div className="space-y-2">
              <button
                onClick={() => {setActiveTab('dashboard'); setSidebarOpen(false);}}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center ${
                  activeTab === 'dashboard' 
                    ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <BarChart3 className="w-5 h-5 mr-3 flex-shrink-0" />
                <span>Dashboard</span>
              </button>
              
              <button
                onClick={() => {setActiveTab('submissions'); setSidebarOpen(false);}}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center ${
                  activeTab === 'submissions' 
                    ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-5 h-5 mr-3 flex-shrink-0" />
                <span>Submissions</span>
                {stats.pending > 0 && (
                  <span className="ml-auto bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {stats.pending}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => {setActiveTab('payments'); setSidebarOpen(false);}}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center ${
                  activeTab === 'payments' 
                    ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <DollarSign className="w-5 h-5 mr-3 flex-shrink-0" />
                <span>Payments</span>
                {stats.approved_pending_payment > 0 && (
                  <span className="ml-auto bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {stats.approved_pending_payment}
                  </span>
                )}
              </button>
            </div>
          </nav>

          {/* Logout Button */}
          <div className="mt-auto p-4 lg:p-6 border-t border-gray-200">
            <button
              onClick={onLogout}
              className="w-full flex items-center px-4 py-3 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-all duration-200"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Main Content Area - Properly responsive */}
        <div className="flex-1 lg:overflow-hidden">
          <div className="h-full lg:h-screen lg:overflow-y-auto">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="max-w-none">
                {activeTab === 'dashboard' && renderDashboard()}
                {activeTab === 'submissions' && renderSubmissions()}
                {activeTab === 'payments' && renderPayments()}
                {activeTab === 'submission-detail' && renderSubmissionDetail()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Action Modal for Payment Amount / Rejection Reason */}
      {showReasonModal && pendingAction && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={cancelAction}
        >
          <div 
            className="bg-white rounded-lg max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {pendingAction.action === 'approve' ? 'Set Payment Amount' : 'Rejection Details'}
              </h3>
              <button
                onClick={cancelAction}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {pendingAction.action === 'approve' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Amount ($)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="5.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Any additional notes..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <select
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Select a reason...</option>
                      <option value="Poor Image Quality - Unable to verify details">Poor Image Quality - Unable to verify details</option>
                      <option value="Document appears altered or tampered">Document appears altered or tampered</option>
                      <option value="Expired Document - Outside validity period">Expired Document - Outside validity period</option>
                      <option value="Personal info doesn't match document">Personal info doesn't match document</option>
                      <option value="Unacceptable document type for this program">Unacceptable document type for this program</option>
                      <option value="Missing required document sides (front/back)">Missing required document sides (front/back)</option>
                      <option value="Document from restricted country/region">Document from restricted country/region</option>
                      <option value="Duplicate submission detected">Duplicate submission detected</option>
                      <option value="Suspicious activity patterns detected">Suspicious activity patterns detected</option>
                      <option value="Document security features not visible/invalid">Document security features not visible/invalid</option>
                      <option value="Age requirements not met">Age requirements not met</option>
                      <option value="Incomplete or missing banking information">Incomplete or missing banking information</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Additional details about the rejection..."
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  pendingAction.action === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {pendingAction.action === 'approve' ? 'Approve & Set Amount' : 'Reject Submission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative w-full h-full max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-70 rounded-full p-3 hover:bg-opacity-90 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-full flex items-center justify-center overflow-hidden">
              <img
                src={selectedImage}
                alt="Document Full Size"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;